import { useCallback } from 'react'
import { supabase } from '~/supabase-client'
import { supabaseGetter } from '~/api/supabase-getter'
import { plotEdited, useCropsContextDispatch } from '~/common/context/crops/crops-context'
import { useMapRefresh } from '~/map/context/use-map-refresh'
import { useSnackbar } from 'notistack'

/**
 * Upserts a plot's polygon boundary using organization_id as the conflict key.
 * organization_polygons uses organization_id as primary key (no separate id column),
 * so we always upsert — insert on first save, update on subsequent saves.
 */
export const useUpsertPlotPolygon = () => {
  const { triggerRefresh } = useMapRefresh()
  const cropsDispatch = useCropsContextDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const upsertPlotPolygon = useCallback(async ({ plotId, points }) => {
    const { error } = await supabase
      .from('organization_polygons')
      .upsert({ organization_id: plotId, points }, { onConflict: 'organization_id' })

    if (error) {
      enqueueSnackbar(error.message, { variant: 'error' })
      return { error }
    }

    supabaseGetter.organizationPolygons.invalidate()
    cropsDispatch(plotEdited({ plotId }))
    triggerRefresh()
    enqueueSnackbar('Plot boundary saved', { variant: 'success' })
    return { error: null }
  }, [triggerRefresh, cropsDispatch, enqueueSnackbar])

  return { upsertPlotPolygon }
}
